import React, { useState, useEffect } from "react";
import { Container, Table, Spinner, Alert, Form, Row, Col, Badge, Button, Modal } from "react-bootstrap";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { db, callXeroFunction } from "../firebase.js";
import { useLanguage } from "../context/LanguageContext.js";
import { useAuth } from "../context/AuthContext.js";
import { t } from "../utils/translations.js";
import { format as fnsFormat } from "date-fns";
import { ptBR, enUS } from "date-fns/locale";

export default function InvoiceHistoryPage() {
  const { language } = useLanguage();
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  // WhatsApp Reminders
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [overdueInvoices, setOverdueInvoices] = useState([]);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [loadingOverdue, setLoadingOverdue] = useState(false);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [reminderResult, setReminderResult] = useState(null);

  // Client Sync
  const [syncingClients, setSyncingClients] = useState(false);

  useEffect(() => {
    fetchInvoices();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredInvoices(invoices);
    } else {
      const filtered = invoices.filter(inv =>
        inv.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        inv.xeroInvoiceNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        inv.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredInvoices(filtered);
    }
  }, [searchTerm, invoices]);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const q = query(collection(db, "xeroInvoices"), orderBy("sentAt", "desc"));
      const snapshot = await getDocs(q);

      const invoicesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setInvoices(invoicesData);
      setFilteredInvoices(invoicesData);
    } catch (error) {
      console.error("Error fetching invoices:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "-";
    const date = timestamp.toDate();
    return fnsFormat(date, "dd MMM yyyy", {
      locale: language === "pt" ? ptBR : enUS
    });
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return "-";
    const date = timestamp.toDate();
    return fnsFormat(date, "dd MMM yyyy HH:mm", {
      locale: language === "pt" ? ptBR : enUS
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(language === "pt" ? "pt-BR" : "en-NZ", {
      style: "currency",
      currency: language === "pt" ? "BRL" : "NZD"
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'DRAFT': { variant: 'secondary', key: 'invoiceDraft' },
      'SUBMITTED': { variant: 'primary', key: 'invoiceSubmitted' },
      'AUTHORISED': { variant: 'success', key: 'invoiceApproved' },
      'PAID': { variant: 'success', key: 'invoicePaid' }
    };

    const statusInfo = statusMap[status] || { variant: 'info', key: null };
    return <Badge bg={statusInfo.variant}>{statusInfo.key ? t(language, statusInfo.key) : status}</Badge>;
  };

  const handleFetchOverdueInvoices = async () => {
    try {
      setLoadingOverdue(true);
      setReminderResult(null);

      const response = await callXeroFunction('xeroOverdueInvoices', {}, { method: 'GET' });

      const invoices = response.invoices || [];
      setOverdueInvoices(invoices);
      setSelectedInvoices([]);
      setShowReminderModal(true);
    } catch (error) {
      console.error("Error fetching overdue invoices:", error);
      alert(t(language, "invoiceErrorOverdue"));
    } finally {
      setLoadingOverdue(false);
    }
  };

  const handleToggleInvoice = (invoice) => {
    const isSelected = selectedInvoices.some(inv => inv.invoiceNumber === invoice.invoiceNumber);

    if (isSelected) {
      setSelectedInvoices(selectedInvoices.filter(inv => inv.invoiceNumber !== invoice.invoiceNumber));
    } else {
      setSelectedInvoices([...selectedInvoices, invoice]);
    }
  };

  const handleSyncClients = async () => {
    try {
      setSyncingClients(true);

      const clientsSnapshot = await getDocs(collection(db, "clients"));
      const clients = clientsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      console.log(`Syncing ${clients.length} clients to PostgreSQL...`);

      const response = await callXeroFunction('syncClientsToPostgres', { clients }, { method: 'POST' });

      alert(t(language, "invoiceSyncSuccess").replace("{count}", response.syncedCount));
      console.log('Sync result:', response);
    } catch (error) {
      console.error("Error syncing clients:", error);
      alert(t(language, "invoiceSyncError"));
    } finally {
      setSyncingClients(false);
    }
  };

  const handleSendReminders = async () => {
    if (selectedInvoices.length === 0) {
      alert(t(language, "invoiceSelectOneInvoice"));
      return;
    }

    try {
      setSendingReminders(true);
      setReminderResult(null);

      const reminders = selectedInvoices.map(inv => ({
        invoiceNumber: inv.invoiceNumber,
        contactName: inv.contactName,
        contactEmail: inv.contactEmail,
        contactPhone: inv.contactPhone || null,
        daysOverdue: inv.daysOverdue,
        amountDue: inv.amountDue,
      }));

      const response = await callXeroFunction('sendInvoiceReminders', { reminders }, { method: 'POST' });

      setReminderResult(response);
      setSelectedInvoices([]);
    } catch (error) {
      console.error("Error sending reminders:", error);
      alert(t(language, "invoiceErrorReminders"));
    } finally {
      setSendingReminders(false);
    }
  };

  if (loading) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">{t(language, "invoiceLoadingInvoices")}</p>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <div className="d-flex justify-content-between flex-wrap gap-2 align-items-center mb-4">
        <h2>{t(language, "invoiceTitle")}</h2>
        <div className="d-flex gap-2">
          <Button
            variant="info"
            onClick={handleSyncClients}
            disabled={syncingClients}
          >
            {syncingClients ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                {t(language, "invoiceSyncing")}
              </>
            ) : (
              <>{t(language, "invoiceSyncClients")}</>
            )}
          </Button>
          <Button
            variant="success"
            onClick={handleFetchOverdueInvoices}
            disabled={loadingOverdue}
          >
            {loadingOverdue ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                {t(language, "loading")}
              </>
            ) : (
              <>{t(language, "invoiceSendReminders")}</>
            )}
          </Button>
        </div>
      </div>

      <Row className="mb-4">
        <Col md={6}>
          <Form.Group>
            <Form.Control
              type="text"
              placeholder={t(language, "invoiceSearchPlaceholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md={6} className="text-end">
          <div className="text-muted">
            {t(language, "invoiceTotalInvoices")} <strong>{filteredInvoices.length}</strong>
            {invoices.length > 0 && (
              <> | {t(language, "invoiceTotalAmount")} <strong>{formatCurrency(filteredInvoices.reduce((sum, inv) => sum + (inv.amount || 0), 0))}</strong></>
            )}
          </div>
        </Col>
      </Row>

      {filteredInvoices.length === 0 ? (
        <Alert variant="info">
          {searchTerm
            ? t(language, "invoiceNoFound")
            : t(language, "invoiceNoSent")
          }
        </Alert>
      ) : (
        <div className="table-responsive">
          <Table responsive striped bordered hover>
            <thead className="table-dark">
              <tr>
                <th className="d-none d-md-table-cell">{t(language, "invoiceNum")}</th>
                <th>{t(language, "invoiceClient")}</th>
                <th>{t(language, "invoiceDate")}</th>
                <th className="d-none d-md-table-cell">{t(language, "invoiceDescription")}</th>
                <th>{t(language, "invoiceAmount")}</th>
                <th>{t(language, "invoiceStatus")}</th>
                <th className="d-none d-md-table-cell">{t(language, "invoiceSentAt")}</th>
                <th className="d-none d-md-table-cell">{t(language, "invoiceSentBy")}</th>
              </tr>
            </thead>
            <tbody>
              {filteredInvoices.map((invoice) => (
                <tr key={invoice.id}>
                  <td className="d-none d-md-table-cell">
                    <strong>{invoice.xeroInvoiceNumber}</strong>
                    <br />
                    <small className="text-muted">{invoice.xeroInvoiceId.substring(0, 8)}...</small>
                  </td>
                  <td>{invoice.clientName}</td>
                  <td>{formatDate(invoice.date)}</td>
                  <td className="d-none d-md-table-cell">
                    {invoice.description}
                    {invoice.notes && (
                      <>
                        <br />
                        <small className="text-muted">{invoice.notes}</small>
                      </>
                    )}
                  </td>
                  <td className="text-end">
                    <strong>{formatCurrency(invoice.amount)}</strong>
                    {invoice.total && invoice.total !== invoice.amount && (
                      <>
                        <br />
                        <small className="text-muted">Total: {formatCurrency(invoice.total)}</small>
                      </>
                    )}
                  </td>
                  <td>{getStatusBadge(invoice.status)}</td>
                  <td className="d-none d-md-table-cell">{formatDateTime(invoice.sentAt)}</td>
                  <td className="d-none d-md-table-cell">
                    <small>{invoice.sentBy}</small>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      {/* WhatsApp Reminders Modal */}
      <Modal
        show={showReminderModal}
        onHide={() => setShowReminderModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {t(language, "invoiceOverdueTitle")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {reminderResult ? (
            <Alert variant={reminderResult.totalSent > 0 ? "success" : "warning"}>
              <h5>
                {t(language, "invoiceRemindersSent")
                  .replace("{sent}", reminderResult.totalSent)
                  .replace("{total}", reminderResult.totalReminders)}
              </h5>
              <ul className="mt-3 mb-0">
                {reminderResult.results.map((result, idx) => (
                  <li key={idx}>
                    <strong>{result.invoiceNumber}</strong> - {result.contactName}:{" "}
                    {result.sent ? (
                      <span className="text-success">
                        ✓ {t(language, "invoiceSent")}
                      </span>
                    ) : (
                      <span className="text-danger">
                        ✗ {result.error || t(language, "invoiceFailed")}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </Alert>
          ) : overdueInvoices.length === 0 ? (
            <Alert variant="info">
              {t(language, "invoiceNoOverdue")}
            </Alert>
          ) : (
            <>
              <Alert variant="warning">
                {language === "en"
                  ? `Found ${overdueInvoices.length} overdue invoice${overdueInvoices.length > 1 ? 's' : ''}. Select which ones to send reminders:`
                  : `Encontradas ${overdueInvoices.length} invoice${overdueInvoices.length > 1 ? 's' : ''} atrasada${overdueInvoices.length > 1 ? 's' : ''}. Selecione quais enviar lembretes:`}
              </Alert>

              <div className="mb-3">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => setSelectedInvoices(overdueInvoices)}
                  className="me-2"
                >
                  {t(language, "invoiceSelectAll")}
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => setSelectedInvoices([])}
                >
                  {t(language, "invoiceDeselectAll")}
                </Button>
              </div>

              <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                <Table striped bordered hover size="sm">
                  <thead>
                    <tr>
                      <th style={{ width: "50px" }}></th>
                      <th>{t(language, "invoiceNum")}</th>
                      <th>{t(language, "invoiceClient")}</th>
                      <th>Email</th>
                      <th>{t(language, "invoiceDaysOverdue")}</th>
                      <th>{t(language, "invoiceAmount")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {overdueInvoices.map((invoice) => {
                      const isSelected = selectedInvoices.some(
                        inv => inv.invoiceNumber === invoice.invoiceNumber
                      );

                      return (
                        <tr
                          key={invoice.invoiceNumber}
                          style={{ cursor: "pointer" }}
                          onClick={() => handleToggleInvoice(invoice)}
                        >
                          <td className="text-center">
                            <Form.Check
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => {}}
                            />
                          </td>
                          <td><strong>{invoice.invoiceNumber}</strong></td>
                          <td>{invoice.contactName}</td>
                          <td><small>{invoice.contactEmail}</small></td>
                          <td className="text-center">
                            <Badge bg={invoice.daysOverdue >= 7 ? "danger" : "warning"}>
                              {invoice.daysOverdue} {t(language, "invoiceDays")}
                            </Badge>
                          </td>
                          <td className="text-end">{formatCurrency(invoice.amountDue)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>

              <Alert variant="info" className="mt-3 mb-0">
                <small>
                  {t(language, "invoiceMessageTemplate")}<br />
                  <em>
                    "Hi [Name], I hope you're doing great! Just a friendly reminder about the invoice [Number], let me know if you have any questions, thank you."
                  </em>
                </small>
              </Alert>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowReminderModal(false);
              setReminderResult(null);
            }}
          >
            {t(language, "close")}
          </Button>
          {!reminderResult && overdueInvoices.length > 0 && (
            <Button
              variant="success"
              onClick={handleSendReminders}
              disabled={sendingReminders || selectedInvoices.length === 0}
            >
              {sendingReminders ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {t(language, "invoiceSending")}
                </>
              ) : (
                <>
                  {t(language, "invoiceSendReminder")} {selectedInvoices.length} {t(language, "invoiceReminder")}{selectedInvoices.length !== 1 ? 's' : ''}
                </>
              )}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
